import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, openUploadDownload } from '../api/client';
import { CertReviewBadge } from '../components/CertReviewBadge';
import {
  Loader2,
  Shield,
  CheckCircle,
  XCircle,
  Flag,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { pendingCertsQuery } from './safety/safety-queries';
import { formatInstantDay } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { Tooltip } from '../components/ui/tooltip';

interface PendingCert {
  id: string;
  certification_type: string;
  certificate_number: string;
  issuing_body: string;
  tech_name: string;
  expiry_date: string;
  submitted_at: string;
  review_status: string;
  review_version: number;
  document_url: string;
  review_notes: string | null;
}

export default function CertificationReviewQueue() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');

  /* The same read the section strip's badge makes — one definition in
   * `safety-queries.ts`, one cache entry, so the badge and this list cannot
   * disagree. It carries a failure branch now: a refused or dropped read used
   * to arrive as `undefined`, `?? []` turned that into an empty queue, and the
   * page said "No certifications pending review" to a reviewer with work
   * waiting. The route is not role-gated; the API decides who may read it. */
  const pendingQuery = useQuery(pendingCertsQuery);
  const { data } = pendingQuery;
  const loadFailure = data === undefined ? queryLoadFailure(pendingQuery) : null;
  const isLoading = data === undefined && !loadFailure;

  const certs: PendingCert[] = data ?? [];

  return (
    /* Under `SafetyLayout`, which owns the band, the <h1> and the strip. The
     * URL stays /cert-review: reviewer notifications link here. */
    <div className="space-y-4">
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
        </div>
      )}

      {loadFailure && (
        <LoadFailureNotice
          error={queryFailureError(pendingQuery)}
          kind={loadFailure}
          subject="certifications awaiting review"
        />
      )}

      {!isLoading && !loadFailure && certs.length === 0 && (
        <div className="text-center py-16">
          <Shield size={48} className="mx-auto text-fieldiq-muted mb-3" />
          <p className="text-fieldiq-text text-sm">No certifications pending review.</p>
        </div>
      )}

      {!isLoading && !loadFailure && certs.length > 0 && (
        <>
          <p className="text-xs text-fieldiq-muted">{certs.length} pending certification{certs.length !== 1 ? 's' : ''}</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-fieldiq-border">
                  <th className="text-left text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2 px-3">Type</th>
                  <th className="text-left text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2 px-3">Cert #</th>
                  <th className="text-left text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2 px-3">Submitted By</th>
                  <th className="text-left text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2 px-3">Submitted</th>
                  <th className="text-left text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2 px-3">Status</th>
                  <th className="text-left text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2 px-3">Document</th>
                  <th className="text-right text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {certs.map(cert => (
                  <CertRow
                    key={cert.id}
                    cert={cert}
                    highlight={cert.id === highlightId}
                    queryClient={queryClient}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function CertRow({
  cert,
  highlight,
  queryClient,
}: {
  cert: PendingCert;
  highlight: boolean;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [actionMode, setActionMode] = useState<'approve' | 'reject' | 'flag' | null>(null);
  const [notes, setNotes] = useState('');
  const timeZone = useTenantTimeZone();

  const approveMutation = useMutation({
    mutationFn: () => api.certifications.approve(cert.id, notes ? { notes } : {}),
    onSuccess: () => {
      toast.success(`${cert.certification_type} approved`);
      queryClient.invalidateQueries({ queryKey: ['certifications-pending'] });
      queryClient.invalidateQueries({ queryKey: ['team-certs'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setActionMode(null);
      setNotes('');
    },
    onError: () => toast.error('Failed to approve certification'),
  });

  const rejectMutation = useMutation({
    mutationFn: () => api.certifications.reject(cert.id, { notes }),
    onSuccess: () => {
      toast.success(`${cert.certification_type} rejected`);
      queryClient.invalidateQueries({ queryKey: ['certifications-pending'] });
      queryClient.invalidateQueries({ queryKey: ['team-certs'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setActionMode(null);
      setNotes('');
    },
    onError: () => toast.error('Failed to reject certification'),
  });

  const flagMutation = useMutation({
    mutationFn: () => api.certifications.flag(cert.id, notes ? { notes } : {}),
    onSuccess: () => {
      toast.success(`${cert.certification_type} flagged`);
      queryClient.invalidateQueries({ queryKey: ['certifications-pending'] });
      queryClient.invalidateQueries({ queryKey: ['team-certs'] });
      setActionMode(null);
      setNotes('');
    },
    onError: () => toast.error('Failed to flag certification'),
  });

  const isSubmitting = approveMutation.isPending || rejectMutation.isPending || flagMutation.isPending;

  const handleSubmitAction = () => {
    if (actionMode === 'approve') approveMutation.mutate();
    else if (actionMode === 'reject') rejectMutation.mutate();
    else if (actionMode === 'flag') flagMutation.mutate();
  };

  // submitted_at is an instant: shown as the TENANT's day. Absent → em dash.
  const formatDate = (d: string) =>
    formatInstantDay(d, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '\u2014';

  const handleOpenDocument = () => {
    if (cert.document_url) {
      // document_url may be a bare UUID or a path like "/api/v1/uploads/download/<uuid>"
      const uuidRe = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
      const match = cert.document_url.match(uuidRe);
      if (match) {
        openUploadDownload(match[0]);
      } else {
        window.open(cert.document_url, '_blank');
      }
    }
  };

  return (
    <>
      <tr className={`border-b border-fieldiq-border/50 hover:bg-secondary/30 transition-colors ${highlight ? 'bg-fi-amber/10' : ''}`}>
        <td className="py-3 px-3 text-sm text-foreground">
          {cert.certification_type ? cert.certification_type.replace(/_/g, ' ') : '\u2014'}
        </td>
        <td className="py-3 px-3 text-sm text-fieldiq-muted font-mono">
          {cert.certificate_number || '\u2014'}
        </td>
        <td className="py-3 px-3 text-sm text-foreground">
          {cert.tech_name || '\u2014'}
          {cert.review_version > 1 && (
            <span className="text-[10px] text-fieldiq-muted ml-1">v{cert.review_version}</span>
          )}
        </td>
        <td className="py-3 px-3 text-xs text-fieldiq-muted">
          {formatDate(cert.submitted_at)}
        </td>
        <td className="py-3 px-3">
          <CertReviewBadge status={cert.review_status} />
        </td>
        <td className="py-3 px-3">
          {cert.document_url ? (
            <button
              onClick={handleOpenDocument}
              className="inline-flex items-center gap-1 text-xs text-fieldiq-accent hover:underline"
            >
              <FileText size={14} /> View <ExternalLink size={10} />
            </button>
          ) : (
            <span className="text-xs text-fieldiq-muted">—</span>
          )}
        </td>
        <td className="py-3 px-3 text-right">
          <div className="flex items-center justify-end gap-1">
            <Tooltip content="Approve">
              <button
                onClick={() => { setActionMode('approve'); setNotes(''); }}
                className="p-1.5 rounded-lg text-fieldiq-text hover:bg-fi-signal-green hover:text-fi-signal-ink transition-colors"
                aria-label="Approve"
              >
                <CheckCircle size={16} />
              </button>
            </Tooltip>
            <Tooltip content="Reject">
              <button
                onClick={() => { setActionMode('reject'); setNotes(''); }}
                className="p-1.5 rounded-lg text-fieldiq-text hover:bg-fi-signal-red hover:text-fi-signal-ink transition-colors"
                aria-label="Reject"
              >
                <XCircle size={16} />
              </button>
            </Tooltip>
            <Tooltip content="Flag">
              <button
                onClick={() => { setActionMode('flag'); setNotes(''); }}
                className="p-1.5 rounded-lg text-fieldiq-text hover:bg-fi-signal-amber hover:text-fi-signal-ink transition-colors"
                aria-label="Flag"
              >
                <Flag size={16} />
              </button>
            </Tooltip>
          </div>
        </td>
      </tr>
      {actionMode && (
        <tr className="border-b border-fieldiq-border/50 bg-fieldiq-dark/50">
          <td colSpan={7} className="px-3 py-3">
            <div className="flex items-start gap-3 max-w-2xl">
              <div className="flex-1">
                <label className="text-xs text-fieldiq-muted block mb-1">
                  {actionMode === 'approve' ? 'Notes (optional)' :
                   actionMode === 'reject' ? 'Reason (required)' :
                   'Notes (optional)'}
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder={actionMode === 'reject' ? 'Provide a reason for rejection...' : 'Add notes...'}
                  rows={2}
                  className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-fieldiq-muted focus:outline-none focus:border-fieldiq-accent resize-none"
                  autoFocus
                />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <button
                  onClick={handleSubmitAction}
                  disabled={isSubmitting || (actionMode === 'reject' && !notes.trim())}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-fi-signal-ink border border-black/20 disabled:opacity-50 transition-opacity hover:opacity-90 ${
                    /* Signal fills with dark ink, which hold in both themes.
                     * Flag was orange; there is no orange hue, and a flag is a
                     * hold for a person to look at, so it takes amber. */
                    actionMode === 'approve' ? 'bg-fi-signal-green' :
                    actionMode === 'reject' ? 'bg-fi-signal-red' :
                    'bg-fi-signal-amber'
                  }`}
                >
                  {isSubmitting && <Loader2 size={12} className="animate-spin" />}
                  {actionMode === 'approve' ? 'Approve' : actionMode === 'reject' ? 'Reject' : 'Flag'}
                </button>
                <button
                  onClick={() => { setActionMode(null); setNotes(''); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-fieldiq-muted hover:text-foreground hover:bg-fieldiq-card transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
